/**
 * O estado vivo do aplicativo do escritório.
 *
 * Um objeto só, com o repositório dentro, criado no arranque e passado às
 * telas. Nada de estado global espalhado: quando uma tela precisa de algo, ela
 * pede aqui, e quando algo muda, `aoMudar` avisa quem estiver na frente.
 *
 * ⚠️ O ESCRITÓRIO É 100% ONLINE (23/09/2026). Não há banco local: o depósito é
 * em MEMÓRIA, recheado pelo servidor no arranque, e cada ação sobe pela RPC na
 * hora. Recarregar a página não perde nada — o Supabase é a verdade, e a
 * verdade desce de novo. A sessão fica no `localStorage` (dentro da `Nuvem`),
 * que é onde todo app online da frota guarda o token.
 */

import { DepositoEmMemoria, Repositorio, type Deposito } from '@pcpgbu/dados';
import { Nuvem, configuracaoDoAmbiente } from '@pcpgbu/offline';
import type { Perfil } from '@pcpgbu/dominio';
import type { Roteador } from '@pcpgbu/ui';

export interface Sessao {
  pessoaId: string | null;
  nome: string;
  perfil: Perfil;
}

export interface Aplicacao {
  repo: Repositorio;
  deposito: Deposito;
  roteador: Roteador;
  sessao: Sessao;
  /** Termo da busca da barra de ferramentas, compartilhado entre telas. */
  busca: string;
  /** Nome da empresa (organização atual), para a casca e o login. */
  organizacaoNome: string | null;
  /** Não há banco local a preservar; o servidor é quem guarda. */
  persistente: boolean;
  /** O servidor: sessão, leitura do conjunto de trabalho e envio das ações. */
  nuvem: Nuvem;
  redesenhar(): void;
}

export async function criarAplicacao(roteador: Roteador): Promise<Omit<Aplicacao, 'redesenhar'>> {
  /* Sem IndexedDB: o conjunto de trabalho vive em memória e vem do servidor. */
  const deposito = new DepositoEmMemoria();
  const repo = new Repositorio(deposito);
  await repo.semear();

  /* O `aoMudar` invalida o repositório quando o servidor traz estado novo —
     sem ele, a tela mostraria o que havia antes da descida. */
  const nuvem = new Nuvem(deposito, configuracaoDoAmbiente(import.meta.env), () => repo.recarregar(), true);
  await nuvem.iniciar();

  /* Cada ação do escritório que tem porta no servidor sobe na hora. O
     `Repositorio` avisa; ninguém precisa lembrar de chamar. Depois de subir,
     puxa de novo para pegar o que o servidor recalculou (status, tempo). */
  repo.aoServidor = (acao) => {
    void nuvem
      .executar(acao.tipo, acao.id, acao.carga)
      .then(() => nuvem.sincronizar())
      .catch(() => undefined);
  };

  /* Arquivos (PDF da OS, imagem da faca) vão para o Storage; o banco guarda o
     caminho, e o detalhe baixa sob demanda. */
  repo.aoEnviarArquivo = (tipo, dados) => nuvem.enviarArquivo(tipo, dados);
  repo.aoBaixarArquivo = (ref) => nuvem.baixarArquivo(ref);

  // Traz o conjunto de trabalho. Com sessão guardada, isto acontece no arranque;
  // sem ela, a tela de entrada loga e o `ligarAutomatico` (no `main.ts`) puxa.
  if (nuvem.logado) {
    await nuvem.sincronizar().catch(() => undefined);
  }

  const cadastros = await repo.carregarCadastros();
  // A pessoa de quem está logado (pelo `user_id`); sem isso, a direção — só
  // para a casca saber o perfil. O servidor é quem decide quem pode o quê.
  const eu =
    cadastros.pessoas.find((p) => p.usuarioId && p.usuarioId === nuvem.userId) ??
    cadastros.pessoas.find((p) => p.perfil === 'direcao');

  // A empresa que veste este PCP. Vem do servidor (o nome da organização);
  // sem sessão, a casca usa um rótulo neutro.
  const organizacaoNome = nuvem.logado ? await nuvem.orgNome().catch(() => null) : null;

  return {
    repo,
    deposito,
    roteador,
    sessao: {
      pessoaId: eu?.id ?? null,
      nome: eu?.nome ?? 'Escritório',
      perfil: eu?.perfil ?? 'direcao',
    },
    busca: '',
    organizacaoNome,
    persistente: true,
    nuvem,
  };
}
